import React, { useMemo } from 'react';
import { Paper, useTheme } from '@mui/material';
import { VictoryBar, VictoryChart, VictoryAxis, VictoryLabel } from 'victory';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';
import { useGetMyAnalyticsData } from 'modules/Insights/Notes/hooks/useGetMyAnalyticsData';
import {
  TimeSignalClassification,
  MyAnalyticsDimensionType
} from 'graphql/types';
import { useCommonChartStyles } from 'modules/Insights/Notes/hooks/useCommonChartStyles';
import { useMessages } from 'modules/intl';
import { useChartStyles } from './hooks/useChartStyles';
import { ClassificationBarSkelton } from './ClassificationBarSkelton';
import { ClassificationBarNoData } from './ClassificationBarNoData';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0, 2),
    width: '34%',
    marginRight: theme.spacing(2)
  }
}));
const classification = [
  TimeSignalClassification.Unclassified,
  TimeSignalClassification.Private,
  TimeSignalClassification.Accepted
];

const labels = [
  'insights.timeClassificationChart.PRIVATE',
  'insights.timeClassificationChart.ACCEPTED',
  'insights.timeClassificationChart.UNCLASSIFIED'
];
export const ClassificationBarChart: React.FC = () => {
  const theme = useTheme();
  const commonStyles = useCommonChartStyles(theme);

  const classificationLabels = useMessages(labels);

  const { data, loading, isEmpty } = useGetMyAnalyticsData(
    MyAnalyticsDimensionType.MetricsByClassification
  );
  const naValueLabel = useMemo(() => {
    const naValueSet = new Set();
    if (data?.myAnalytics?.items) {
      const chartDataSet = new Set();
      data?.myAnalytics?.items?.dimensions.forEach((item) => {
        chartDataSet.add(item.id);
      });
      const value = classification.filter((item) => {
        if (chartDataSet.has(item) == false) {
          naValueSet.add(
            classificationLabels[`insights.timeClassificationChart.${item}`]
          );
          return item;
        }
      });
      return { set: naValueSet, array: value };
    }
    return { array: [] };
  }, [classificationLabels, data?.myAnalytics?.items]);

  const chartStyle = useChartStyles(theme);
  const styles = useStyles();
  const { formatMessage } = useIntl();
  const chartData = useMemo(() => {
    const metrics = data?.myAnalytics?.items?.metrics || [];
    return (data?.myAnalytics?.items?.dimensions || [])
      .map((d, i) => {
        if (d.id === TimeSignalClassification.Trash) {
          return;
        }
        return {
          x: classificationLabels[`insights.timeClassificationChart.${d.id}`],
          y: metrics[i]!.totalCount,
          color: chartStyle.VictoryBar.color[d.id!]
        };
      })
      .filter((d) => d)
      .sort((a, b) => a!.y - b!.y);
  }, [
    data?.myAnalytics?.items?.metrics,
    data?.myAnalytics?.items?.dimensions,
    classificationLabels,
    chartStyle.VictoryBar.color
  ]);
  const chartDataItems = useMemo(() => {
    const maxCount = chartData.length;
    const data = naValueLabel.array.map((item) => {
      return {
        x: classificationLabels[`insights.timeClassificationChart.${item}`],
        y: (chartData[maxCount - 1]?.y || 1) * 0.01,
        color: chartStyle.VictoryBar.color[item]
      };
    });
    return [...data, ...chartData];
  }, [
    chartData,
    chartStyle.VictoryBar.color,
    naValueLabel.array,
    classificationLabels
  ]);
  return (
    <Paper
      className={styles.container}
      elevation={3}
      data-testid={'notes-classification-container'}
      tabIndex={0}
    >
      {loading ? (
        <ClassificationBarSkelton />
      ) : isEmpty ? (
        <ClassificationBarNoData />
      ) : (
        <>
          <VictoryChart
            height={chartStyle.VictoryChart.height}
            padding={chartStyle.VictoryChart.padding}
            domainPadding={chartStyle.VictoryChart.domainPadding}
            animate={{
              onLoad: { duration: 500 },
              easing: 'bounce'
            }}
          >
            <VictoryAxis offsetX={14} style={chartStyle.VictoryAxis.style} />
            <VictoryBar
              horizontal={true}
              barWidth={chartStyle.VictoryBar.barWidth}
              labels={({ datum }) =>
                !naValueLabel.set?.has(datum.x)
                  ? `(${datum.y})`
                  : formatMessage({
                      id: 'insights.timeSheetLabels.na'
                    })
              }
              style={{
                data: { fill: ({ datum }) => datum?.color },
                labels: {
                  fontSize: commonStyles.Labels.fontSize,
                  fontFamily: commonStyles.Labels.fontFamily
                }
              }}
              labelComponent={
                <VictoryLabel
                  dx={({ datum }) =>
                    !naValueLabel.set?.has(datum.x)
                      ? theme.spacing(2.5)
                      : theme.spacing(40)
                  }
                />
              }
              data={chartDataItems}
            />
          </VictoryChart>
        </>
      )}
    </Paper>
  );
};
